import { Skeleton } from "@/components/ui/skeleton";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import i18n from "@/i18n";
import { cn, formatIndicatorImpactUnitLabel } from "@/libs/utils";
import { useEffect, useMemo, useRef, useState } from "react";

type IndicatorLang = {
  name?: string | null;
  lang?: {
    code?: string | null;
  } | null;
};

type IndicatorDefinition = {
  id?: number | null;
  name?: string | null;
  code?: string | null;
  unity?: string | null;
  moyenne?: boolean | null;
  programReporting?: string | null;
  indicatorLangs?: IndicatorLang[] | null;
};

type ProjectMetaResponse = {
  id?: number | null;
  program?: {
    id?: number | null;
  } | null;
  indicators?: IndicatorDefinition[] | null;
  indicatorOrder?: number[] | null;
};

type ReportingIndicatorTotal = {
  indicator?: IndicatorDefinition | null;
  type?: number | null;
  total?: number | null;
  count?: number | null;
};

type ReportingProjectIndicator = {
  indicator?: IndicatorDefinition | null;
  type?: number | null;
  total?: number | null;
  count?: number | null;
  yearValue?: number | null;
  month?: number | null;
  year?: number | null;
};

type ReportingResponse = {
  projectTotalIndicators?: ReportingIndicatorTotal[] | null;
  projectIndicators?: ReportingProjectIndicator[] | null;
};

type ImpactCardItem = {
  id: number;
  label: string;
  unity: string;
  planned: number;
  realized: number;
};

const IMPACT_CARD_COLOR_CLASSES = [
  "border-verdeoorange/20 bg-verdeoorange/10 text-verdeoorange",
  "border-verdeoblue/20 bg-verdeoblue/10 text-verdeoblue",
  "border-verdeogray/20 bg-verdeogray/10 text-verdeogray",
  "border-verdeored/20 bg-verdeored/10 text-verdeored",
  "border-verdeogreen/20 bg-verdeogreen/10 text-verdeogreen",
];

function toTrimmedText(value: unknown) {
  return String(value ?? "").trim();
}

function getIndicatorLabel(indicator: IndicatorDefinition) {
  const languageCode = toTrimmedText(i18n.language).toLowerCase() || "fr";
  const langs = Array.isArray(indicator.indicatorLangs)
    ? indicator.indicatorLangs
    : [];

  const exactMatch = langs.find(
    (item) =>
      toTrimmedText(item?.lang?.code).toLowerCase() === languageCode
      && toTrimmedText(item?.name),
  );
  if (exactMatch?.name) return toTrimmedText(exactMatch.name);

  const frenchMatch = langs.find(
    (item) =>
      toTrimmedText(item?.lang?.code).toLowerCase() === "fr"
      && toTrimmedText(item?.name),
  );
  if (frenchMatch?.name) return toTrimmedText(frenchMatch.name);

  const fallbackLang = langs.find((item) => toTrimmedText(item?.name));
  if (fallbackLang?.name) return toTrimmedText(fallbackLang.name);

  return toTrimmedText(indicator.name) || "Indicateur";
}

function getIndicatorUnity(indicator: IndicatorDefinition) {
  return formatIndicatorImpactUnitLabel(indicator.unity);
}

function orderProjectIndicators(
  indicators: IndicatorDefinition[] | null | undefined,
  indicatorOrder: number[] | null | undefined,
) {
  const rawIndicators = Array.isArray(indicators) ? indicators : [];
  const orderMap = new Map<number, number>(
    (Array.isArray(indicatorOrder) ? indicatorOrder : []).map((id, index) => [
      Number(id),
      index,
    ]),
  );
  const uniqueById = new Map<number, IndicatorDefinition>();

  rawIndicators.forEach((indicator) => {
    const indicatorId = Number(indicator?.id || 0);
    if (!indicatorId || uniqueById.has(indicatorId)) return;
    uniqueById.set(indicatorId, indicator);
  });

  return Array.from(uniqueById.values())
    .filter((indicator) => {
      const indicatorId = Number(indicator?.id || 0);
      const indicatorCode = toTrimmedText(indicator?.code).toLowerCase();
      return (
        indicatorId > 0
        && indicatorId !== 2
        && indicatorCode !== "euro_recus"
        && indicator?.programReporting !== null
      );
    })
    .sort((left, right) => {
      const leftId = Number(left?.id || 0);
      const rightId = Number(right?.id || 0);
      const leftOrder = orderMap.has(leftId)
        ? Number(orderMap.get(leftId))
        : Number.POSITIVE_INFINITY;
      const rightOrder = orderMap.has(rightId)
        ? Number(orderMap.get(rightId))
        : Number.POSITIVE_INFINITY;

      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return getIndicatorLabel(left).localeCompare(
        getIndicatorLabel(right),
        "fr",
      );
    });
}

function buildImpactCards(
  indicators: IndicatorDefinition[],
  totals: ReportingIndicatorTotal[] | null | undefined,
) {
  const totalsByIndicatorId = new Map<
    number,
    {
      planned: number;
      realized: number;
    }
  >();

  (Array.isArray(totals) ? totals : []).forEach((row) => {
    const indicatorId = Number(row?.indicator?.id || 0);
    if (!indicatorId) return;

    const current = totalsByIndicatorId.get(indicatorId) || {
      planned: 0,
      realized: 0,
    };
    const type = Number(row?.type || 0);
    const total = Number(row?.total || 0);
    const count = Number(row?.count || 0);
    const isAverage = Boolean(row?.indicator?.moyenne);

    if (type === 1) current.planned = isAverage && count > 0 ? total / count : total;
    if (type === 2) current.realized = isAverage && count > 0 ? total / count : total;

    totalsByIndicatorId.set(indicatorId, current);
  });

  return indicators.map((indicator) => {
    const indicatorId = Number(indicator.id || 0);
    const totalsForIndicator = totalsByIndicatorId.get(indicatorId) || {
      planned: 0,
      realized: 0,
    };

    return {
      id: indicatorId,
      label: getIndicatorLabel(indicator),
      unity: getIndicatorUnity(indicator),
      planned: totalsForIndicator.planned,
      realized: totalsForIndicator.realized,
    } satisfies ImpactCardItem;
  });
}

function mergeReportingTotals(
  responses: Array<ReportingResponse | null | undefined>,
) {
  const totalsByKey = new Map<string, ReportingIndicatorTotal>();

  responses.forEach((response) => {
    (Array.isArray(response?.projectTotalIndicators)
      ? response?.projectTotalIndicators
      : []
    ).forEach((row) => {
      const indicatorId = Number(row?.indicator?.id || 0);
      const type = Number(row?.type || 0);
      if (!indicatorId || !type) return;

      const key = `${indicatorId}:${type}`;
      const current = totalsByKey.get(key);
      const rowTotal = Number(row?.total || 0);

      if (!current) {
        totalsByKey.set(key, {
          indicator: row?.indicator || null,
          type,
          total: Number.isFinite(rowTotal) ? rowTotal : 0,
          count: Number.isFinite(Number(row?.count)) ? Number(row?.count) : 0,
        });
        return;
      }

      totalsByKey.set(key, {
        indicator: current.indicator || row?.indicator || null,
        type,
        total:
          Number(current.total || 0) + (Number.isFinite(rowTotal) ? rowTotal : 0),
        count:
          Number(current.count || 0) +
          (Number.isFinite(Number(row?.count)) ? Number(row?.count) : 0),
      });
    });
  });

  return Array.from(totalsByKey.values());
}

function mergeReportingTotalsForYear(
  responses: Array<ReportingResponse | null | undefined>,
  yearFilter: string | null | undefined,
) {
  const normalizedYear =
    yearFilter && yearFilter !== "all" ? Number(yearFilter) : null;

  if (!normalizedYear || !Number.isFinite(normalizedYear)) {
    return mergeReportingTotals(responses);
  }

  const totalsByKey = new Map<string, ReportingIndicatorTotal>();

  responses.forEach((response) => {
    const latestValuesByIndicatorKey = new Map<
      string,
      {
        indicator: IndicatorDefinition | null;
        type: number;
        month: number;
        value: number;
        count: number;
      }
    >();

    (Array.isArray(response?.projectIndicators)
      ? response.projectIndicators
      : []
    ).forEach((row) => {
      const indicatorId = Number(row?.indicator?.id || 0);
      const type = Number(row?.type || 0);
      const year = Number(row?.year || 0);
      if (!indicatorId || (type !== 1 && type !== 2) || year !== normalizedYear) {
        return;
      }

      const key = `${indicatorId}:${type}`;
      const currentMonth = Number(row?.month ?? -1);
      const value = Number(row?.yearValue ?? row?.total ?? 0);
      const count = Number(row?.count || 0);
      const existing = latestValuesByIndicatorKey.get(key);

      if (!existing || currentMonth >= existing.month) {
        latestValuesByIndicatorKey.set(key, {
          indicator: row?.indicator || null,
          type,
          month: currentMonth,
          value: Number.isFinite(value) ? value : 0,
          count: Number.isFinite(count) ? count : 0,
        });
      }
    });

    latestValuesByIndicatorKey.forEach((entry, key) => {
      const current = totalsByKey.get(key);
      if (!current) {
        totalsByKey.set(key, {
          indicator: entry.indicator,
          type: entry.type,
          total: entry.value,
          count: entry.count,
        });
        return;
      }

      totalsByKey.set(key, {
        indicator: current.indicator || entry.indicator,
        type: entry.type,
        total: Number(current.total || 0) + entry.value,
        count: Number(current.count || 0) + Number(entry.count || 0),
      });
    });
  });

  return Array.from(totalsByKey.values());
}

function formatImpactValue(value: number, unity: string) {
  return `${Math.round(Number(value || 0)).toLocaleString("fr-FR")}${unity ? ` ${unity}` : ""}`;
}

function ImpactCard({
  item,
  colorClassName,
  compact = false,
}: {
  item: ImpactCardItem;
  colorClassName: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        compact
          ? "w-[170px] shrink-0 overflow-hidden rounded-lg border px-2 py-2"
          : "w-[196px] shrink-0 overflow-hidden rounded-xl border px-2.5 py-2.5",
        colorClassName,
      )}
      title={item.label}
    >
      <div className={cn(compact ? "space-y-0.5" : "space-y-1")}>
        <div
          className={cn(
            "line-clamp-2 font-semibold",
            compact
              ? "min-h-[28px] text-[11px] leading-[14px]"
              : "min-h-[32px] text-[12px] leading-4",
          )}
        >
          {item.label} prévu
        </div>
        <div className={cn(compact ? "text-base" : "text-lg", "font-semibold")}>
          {formatImpactValue(item.planned, item.unity)}
        </div>
        <div className={cn(compact ? "text-[10px]" : "text-[11px]", "text-slate-700")}>
          Déjà réalisé : {formatImpactValue(item.realized, item.unity)}
        </div>
      </div>
    </div>
  );
}

export default function SynthesesProjectImpactIndicators({
  projectId,
  projectIds,
  className,
  title = "Impact du projet",
  description = "Totaux des indicateurs du projet, avec le prévu et le déjà réalisé.",
  compact = false,
  yearFilter,
  embedded = false,
  autoLoad = false,
}: {
  projectId?: number | null | undefined;
  projectIds?: number[] | null | undefined;
  className?: string;
  title?: string;
  description?: string;
  compact?: boolean;
  yearFilter?: string | null;
  embedded?: boolean;
  autoLoad?: boolean;
}) {
  const { fetchData } = useFetchDataV2("", {
    browserCache: true,
    browserCacheNamespace: "syntheses-impact",
  });
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const contentInnerRef = useRef<HTMLDivElement | null>(null);
  const scrollTrackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
  } | null>(null);
  const normalizedProjectIds = useMemo(() => {
    const rawIds = Array.isArray(projectIds)
      ? projectIds
      : Number(projectId || 0) > 0
        ? [Number(projectId)]
        : [];

    return Array.from(
      new Set(
        rawIds
          .map((value) => Number(value || 0))
          .filter((value) => Number.isFinite(value) && value > 0),
      ),
    );
  }, [projectId, projectIds]);
  const projectIdsKey = useMemo(
    () => normalizedProjectIds.join(","),
    [normalizedProjectIds],
  );
  const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
    .trim()
    .replace(/\/+$/, "");
  const [projectMetas, setProjectMetas] = useState<ProjectMetaResponse[]>([]);
  const [reportingResponses, setReportingResponses] = useState<ReportingResponse[]>(
    [],
  );
  const [isLoadRequested, setIsLoadRequested] = useState(autoLoad);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [scrollMetrics, setScrollMetrics] = useState({
    viewportWidth: 0,
    contentWidth: 0,
    maxScrollLeft: 0,
  });
  const [scrollLeft, setScrollLeft] = useState(0);

  const orderedIndicators = useMemo(
    () => {
      const mergedIndicators = projectMetas.flatMap((meta) =>
        Array.isArray(meta?.indicators) ? meta.indicators : [],
      );
      const mergedIndicatorOrder = projectMetas.flatMap((meta) =>
        Array.isArray(meta?.indicatorOrder) ? meta.indicatorOrder : [],
      );
      return orderProjectIndicators(mergedIndicators, mergedIndicatorOrder);
    },
    [projectMetas],
  );

  const cards = useMemo(
    () =>
      buildImpactCards(
        orderedIndicators,
        mergeReportingTotalsForYear(reportingResponses, yearFilter),
      ),
    [orderedIndicators, reportingResponses, yearFilter],
  );

  useEffect(() => {
    if (autoLoad) {
      setIsLoadRequested(true);
    }
  }, [autoLoad]);

  useEffect(() => {
    if (!autoLoad) {
      setIsLoadRequested(false);
    }
  }, [autoLoad, projectIdsKey]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isLoadRequested) {
        setProjectMetas([]);
        setReportingResponses([]);
        setIsLoading(false);
        setHasError(false);
        return;
      }

      if (!apiBase || normalizedProjectIds.length === 0) {
        setProjectMetas([]);
        setReportingResponses([]);
        setIsLoading(false);
        setHasError(false);
        return;
      }

      setIsLoading(true);
      setHasError(false);

      try {
        const projectMetaResults = await Promise.all(
          normalizedProjectIds.map((numericProjectId) =>
            fetchData(`${apiBase}/project/${numericProjectId}?excludes=reporting`),
          ),
        );
        if (cancelled) return;

        const nextProjectMetas = projectMetaResults.filter(
          (item): item is ProjectMetaResponse =>
            !!item && typeof item === "object" && Number(item?.id || 0) > 0,
        );
        setProjectMetas(nextProjectMetas);

        const projectsByProgramId = new Map<number, number[]>();
        nextProjectMetas.forEach((meta) => {
          const numericProjectId = Number(meta?.id || 0);
          const programId = Number(meta?.program?.id || 0);
          if (!numericProjectId || !programId) return;
          const current = projectsByProgramId.get(programId) || [];
          current.push(numericProjectId);
          projectsByProgramId.set(programId, current);
        });

        const reportingResults = await Promise.all(
          Array.from(projectsByProgramId.entries()).map(([programId, ids]) =>
            fetchData(
              `${apiBase}/reporting?program=${programId}&projects=${ids.join(",")}&isForProject=1`,
            ),
          ),
        );
        if (cancelled) return;

        setReportingResponses(
          reportingResults.filter(
            (item): item is ReportingResponse =>
              !!item && typeof item === "object",
          ),
        );
      } catch {
        if (!cancelled) {
          setHasError(true);
          setProjectMetas([]);
          setReportingResponses([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBase, projectIdsKey, isLoadRequested]);

  useEffect(() => {
    const viewportNode = contentScrollRef.current;
    const contentNode = contentInnerRef.current;
    if (!viewportNode || !contentNode) return;

    const updateMetrics = () => {
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = contentNode.scrollWidth;
      const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

      setScrollMetrics({
        viewportWidth,
        contentWidth,
        maxScrollLeft,
      });
      setScrollLeft((current) => Math.min(current, maxScrollLeft));
    };

    updateMetrics();

    const observer = new ResizeObserver(() => {
      updateMetrics();
    });
    observer.observe(viewportNode);
    observer.observe(contentNode);

    return () => {
      observer.disconnect();
    };
  }, [cards.length, isLoading]);

  const syncScrollLeft = (nextScrollLeft: number) => {
    const viewportNode = contentScrollRef.current;
    const maxScrollLeft = scrollMetrics.maxScrollLeft;
    const clampedValue = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft));

    if (viewportNode) {
      viewportNode.scrollLeft = clampedValue;
    }
    setScrollLeft(clampedValue);
  };

  const handleContentScroll = () => {
    const viewportNode = contentScrollRef.current;
    if (!viewportNode) return;
    setScrollLeft(viewportNode.scrollLeft);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const trackNode = scrollTrackRef.current;
    if (!trackNode || scrollMetrics.maxScrollLeft <= 0) return;

    const bounds = trackNode.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    syncScrollLeft(ratio * scrollMetrics.maxScrollLeft);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (scrollMetrics.maxScrollLeft <= 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollLeft,
    };

    const releaseDrag = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      const trackNode = scrollTrackRef.current;
      if (!state || !trackNode) return;
      if (moveEvent.pointerId !== state.pointerId) return;

      const deltaX = moveEvent.clientX - state.startClientX;
      const trackWidth = trackNode.clientWidth;
      if (trackWidth <= 0) return;

      const nextScrollLeft =
        state.startScrollLeft +
        (deltaX / trackWidth) * scrollMetrics.maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
  };

  const thumbWidthPercent =
    scrollMetrics.contentWidth > 0
      ? Math.max(
          12,
          Math.min(
            100,
            (scrollMetrics.viewportWidth / scrollMetrics.contentWidth) * 100,
          ),
        )
      : 100;
  const thumbTravelPercent = Math.max(0, 100 - thumbWidthPercent);
  const thumbLeftPercent =
    scrollMetrics.maxScrollLeft > 0
      ? (scrollLeft / scrollMetrics.maxScrollLeft) * thumbTravelPercent
      : 0;

  if (normalizedProjectIds.length === 0) return null;

  return (
    <div
      className={cn(
        embedded
          ? "min-w-0 w-full max-w-full"
          : "min-w-0 w-full max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white",
        className,
      )}
    >
      <div
        className={cn(
          embedded
            ? compact
              ? "px-0 pb-2"
              : "px-0 pb-2.5"
            : "border-b border-slate-200 bg-slate-50/70",
          !embedded && (compact ? "px-2.5 py-2" : "px-3 py-2.5"),
        )}
      >
        <div
          className={cn(
            embedded
              ? compact
                ? "text-sm font-semibold text-slate-900"
                : "text-base font-semibold text-slate-900"
              : compact
                ? "text-[9px] font-semibold uppercase tracking-wide text-slate-500"
                : "text-[10px] font-semibold uppercase tracking-wide text-slate-500",
          )}
        >
          {title}
        </div>
        <p
          className={cn(
            "mt-0.5",
            embedded
              ? compact
                ? "text-xs text-black/65"
                : "text-sm text-black/65"
              : compact
                ? "text-[10px] text-slate-500"
                : "text-[11px] text-slate-500",
          )}
        >
          {description}
        </p>
      </div>

      <div className={cn(embedded ? (compact ? "p-0" : "p-0.5") : compact ? "p-2" : "p-2.5")}>
        {!isLoadRequested ? (
          <div
            className={cn(
              "rounded-xl border border-dashed border-slate-200 bg-white/70",
              compact ? "p-2.5" : "p-3",
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div
                  className={cn(
                    "font-medium text-slate-700",
                    compact ? "text-xs" : "text-sm",
                  )}
                >
                  Indicateurs non chargés automatiquement
                </div>
                <p
                  className={cn(
                    "mt-0.5 text-slate-500",
                    compact ? "text-[11px]" : "text-xs",
                  )}
                >
                  Les données de reporting des projets sont chargées uniquement
                  à la demande pour éviter de ralentir la page.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsLoadRequested(true)}
                className={cn(
                  "shrink-0 rounded-lg border border-slate-300 bg-white font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50",
                  compact ? "px-2.5 py-1.5 text-[11px]" : "px-3 py-2 text-xs",
                )}
              >
                Charger les indicateurs
              </button>
            </div>
          </div>
        ) : isLoading && cards.length === 0 ? (
          <div className={cn("flex overflow-hidden", compact ? "gap-2" : "gap-3")}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton
                key={`impact-skeleton-${projectIdsKey || "none"}-${index}`}
                className={cn(
                  "shrink-0",
                  compact ? "h-[84px] w-[180px] rounded-lg" : "h-[104px] w-[220px] rounded-xl",
                )}
              />
            ))}
          </div>
        ) : hasError && cards.length === 0 ? (
          <div className={cn(compact ? "text-xs" : "text-sm", "text-slate-500")}>
            Indicateurs indisponibles pour le moment.
          </div>
        ) : cards.length > 0 ? (
          <div className="min-w-0 w-full max-w-full">
            <div
              ref={contentScrollRef}
              className={cn(
                "hide-native-scrollbar min-w-0 w-full max-w-full overflow-x-auto",
                embedded ? "pb-1.5" : "pb-2",
              )}
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
              onScroll={handleContentScroll}
            >
              <div
                ref={contentInnerRef}
                className={cn("flex w-max pr-1", compact ? "gap-2" : "gap-3")}
              >
                {cards.map((card, index) => (
                  <ImpactCard
                    key={`impact-card-${projectIdsKey || "none"}-${card.id}`}
                    item={card}
                    compact={compact}
                    colorClassName={
                      IMPACT_CARD_COLOR_CLASSES[
                        index % IMPACT_CARD_COLOR_CLASSES.length
                      ]
                    }
                  />
                ))}
              </div>
            </div>

            {scrollMetrics.maxScrollLeft > 0 ? (
              <div className={cn(compact ? "px-0.5 pt-0.5" : "px-1")}>
                <div
                  ref={scrollTrackRef}
                  className={cn(
                    "relative w-full cursor-pointer rounded-full bg-slate-200/90",
                    compact ? "h-2.5" : "h-3",
                  )}
                  onPointerDown={handleTrackPointerDown}
                >
                  <div
                    role="slider"
                    aria-label="Défilement horizontal des indicateurs"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(scrollMetrics.maxScrollLeft)}
                    aria-valuenow={Math.round(scrollLeft)}
                    tabIndex={0}
                    onPointerDown={handleThumbPointerDown}
                    className={cn(
                      "absolute rounded-full bg-slate-400 shadow-sm transition-colors hover:bg-slate-500",
                      compact ? "top-[2px] h-[6px]" : "top-[2px] h-[8px]",
                    )}
                    style={{
                      left: `${thumbLeftPercent}%`,
                      width: `${thumbWidthPercent}%`,
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className={cn(compact ? "text-xs" : "text-sm", "text-slate-500")}>
            Aucun indicateur à afficher.
          </div>
        )}
      </div>
    </div>
  );
}
